import { Typography, CircularProgress, Grid, Chip } from "@material-ui/core";
import { Breadcrumb } from "components/breadCrumb";
import { PageTitle } from "components/pageTitle";
import React, { useEffect } from "react";
import { useParams } from "react-router";
import { Wrapper } from "components/wrapper";
import { DetailCard, DetailField, DetailSectionTitle } from "components/detailPage";
import { DocumentList } from "components/documentList";
import { ExportButtons } from "components/exportButtons";
import { useConvenioDetailsState } from "./context/convenio-details-context";
import { useConvenioDetailsController } from "./context/convenio-details-controller";

const formatCurrency = (value?: number | null) =>
  value !== undefined && value !== null
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '-';

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('pt-BR') : '-';

export function ConvenioDetailsPage() {
  const { convenioDetails, loading, error } = useConvenioDetailsState();
  const { loadConvenioDetails, clearConvenioDetails, exportConvenioDetail } = useConvenioDetailsController();
  let { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (id) {
      loadConvenioDetails(parseInt(id));
    }

    return () => {
      clearConvenioDetails();
    };
  }, [id, loadConvenioDetails, clearConvenioDetails]);

  if (loading) {
    return (
      <Wrapper>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </div>
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <Typography color="error" variant="h6">{error}</Typography>
      </Wrapper>
    );
  }

  if (!convenioDetails) {
    return (
      <Wrapper>
        <Typography variant="h6">Convênio não encontrado.</Typography>
      </Wrapper>
    );
  }

  return (
    <>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Convênios", href: "/convenios" },
          { label: convenioDetails.numeroConvenio },
        ]}
      />
      <PageTitle>CONVÊNIO {convenioDetails.numeroConvenio}</PageTitle>
      <Wrapper>
        <ExportButtons onExport={(format) => exportConvenioDetail(Number(id), format)} />
        <DetailCard>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Chip label={convenioDetails.situacaoName} color="primary" />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DetailField label="Órgão" value={convenioDetails.orgaoName} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DetailField label="Concedente" value={convenioDetails.nomConcedente} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DetailField label="Convenente" value={convenioDetails.nomConvenente} />
            </Grid>

            <Grid item xs={12}>
              <DetailSectionTitle>Vigência e Valores</DetailSectionTitle>
            </Grid>

            <Grid item xs={12} sm={3}>
              <DetailField label="Celebração" value={formatDate(convenioDetails.dtaCelebracao)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DetailField label="Início da Vigência" value={formatDate(convenioDetails.dtaInicioVigencia)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DetailField label="Fim da Vigência" value={formatDate(convenioDetails.dtaFimVigencia)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DetailField label="Publicação" value={formatDate(convenioDetails.dtaPublicacao)} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <DetailField label="Valor do Convênio" value={formatCurrency(convenioDetails.valorConvenio)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailField label="Contrapartida" value={formatCurrency(convenioDetails.valorContrapartida)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailField label="Valor Repassado" value={formatCurrency(convenioDetails.valorRepassado)} />
            </Grid>

            <Grid item xs={12}>
              <DetailSectionTitle>Detalhes</DetailSectionTitle>
            </Grid>

            <Grid item xs={12}>
              <DetailField label="Objeto" value={convenioDetails.textoObjetoConvenio} />
            </Grid>

            {convenioDetails.textoJustificativaConvenio && (
              <Grid item xs={12}>
                <DetailField label="Justificativa" value={convenioDetails.textoJustificativaConvenio} />
              </Grid>
            )}

            <Grid item xs={12}>
              <DetailSectionTitle>Documentos</DetailSectionTitle>
              <DocumentList documents={convenioDetails.documentos} />
            </Grid>
          </Grid>
        </DetailCard>
      </Wrapper>
    </>
  );
}
